import {
  AdsPowerLogo,
  AmazonLogo,
  Badge,
  BingLogo,
  BrowserAutomationStudioLogo,
  BytedanceLogo,
  ChromeLogo,
  ChromiumLogo,
  ClaudeLogo,
  CodexLogo,
  CopilotLogo,
  CursorLogo,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DuckDuckGoLogo,
  GeminiLogo,
  GrokLogo,
  LinkButton,
  NightmareLogo,
  OpenAILogo,
  PerplexityLogo,
  PuppeteerLogo,
  SeleniumLogo,
  Typography,
  WebDriverLogo,
} from '@compass/components'
import { AppRoute } from 'appRoutes'
import { LinkButtonLink } from 'components/compass-integration'
import {
  DOCS_AUTOMATION_INTELLIGENCE,
  DOCS_BOTD_QUICK_START_URL,
  SubscriptionDisplayPlan,
  SUPPORT_PAGE_URL,
} from 'const'
import { Distribution } from 'features/commonUI'
import { getSubscriptionDisplayPlan } from 'helpers/subscription'
import { useSubscription } from 'hooks/api/subscriptions'
import { useCurrentPathname } from 'hooks/useCurrentPathname'
import { ExternalLink } from 'lucide-react'
import { ampli } from 'models/ampli'
import { ComponentType, ReactNode, useState } from 'react'
import { Overlay } from 'react-aria/Overlay'

import { BotDetectionSource } from '../models'
import { GridIcon } from './GridIcon/GridIcon'

interface BotDetectionUpsellModalProps {
  isOpen: boolean
  subscriptionId: string
  /**
   * Which product the workspace is missing. Mirrors the Overview source tabs: `edge` upsells the
   * Automation Intelligence API, `identification` upsells the Bot Detection (browser bots) product.
   */
  source?: BotDetectionSource
}

type PreviewRow = {
  label: string
  Logo: ComponentType<{ size?: number | string }>
  percentage: number
  value: string
}

type PreviewBot = Omit<PreviewRow, 'percentage' | 'value'>

const PREVIEW_PERCENTAGES = [0.6, 0.25, 0.1, 0.04, 0.01] as const
const PREVIEW_ROW_COUNT = PREVIEW_PERCENTAGES.length
const PREVIEW_LEADER_MIN = 1_000
const PREVIEW_LEADER_MAX = 50_000

const BOT_NAMES: PreviewBot[] = [
  { label: 'OpenAI Agent', Logo: OpenAILogo },
  { label: 'Claude Bot', Logo: ClaudeLogo },
  { label: 'Gemini', Logo: GeminiLogo },
  { label: 'Perplexity Agent', Logo: PerplexityLogo },
  { label: 'Copilot', Logo: CopilotLogo },
  { label: 'Cursor Agent', Logo: CursorLogo },
  { label: 'Grok', Logo: GrokLogo },
  { label: 'Codex Agent', Logo: CodexLogo },
  { label: 'Bingbot', Logo: BingLogo },
  { label: 'Amazonbot', Logo: AmazonLogo },
  { label: 'Bytespider', Logo: BytedanceLogo },
  { label: 'DuckDuckBot', Logo: DuckDuckGoLogo },
  { label: 'HeadlessChrome', Logo: ChromeLogo },
]

const AUTOMATION_TOOL_NAMES: PreviewBot[] = [
  { label: 'Puppeteer', Logo: PuppeteerLogo },
  { label: 'Selenium', Logo: SeleniumLogo },
  { label: 'WebDriver', Logo: WebDriverLogo },
  { label: 'HeadlessChrome', Logo: ChromeLogo },
  { label: 'Chromium', Logo: ChromiumLogo },
  { label: 'Nightmare', Logo: NightmareLogo },
  { label: 'AdsPower', Logo: AdsPowerLogo },
  { label: 'Browser Automation Studio', Logo: BrowserAutomationStudioLogo },
]

type UpsellContent = {
  title: string
  description: string
  body: ReactNode
  previewTitle: string
  previewLabel: string
  previewBots: PreviewBot[]
  docsUrl: string
}

const UPSELL_CONTENT: Record<BotDetectionSource, UpsellContent> = {
  edge: {
    title: 'Enable Automation Intelligence',
    description: 'Add frictionless bot and AI detection to your app.',
    body: (
      <p>
        Maintain a user experience that is seamless, agent-friendly, and safe against even the most sophisticated
        malicious bots. And inspect the <b>trusted</b>, <b>spoofed</b>, and <b>AI</b> bots visiting your app.
      </p>
    ),
    previewTitle: 'Top bot names',
    previewLabel: 'Preview of the individual bots detected most often across the workspace.',
    previewBots: BOT_NAMES,
    docsUrl: DOCS_AUTOMATION_INTELLIGENCE,
  },
  identification: {
    title: 'Enable Bot Detection',
    description: 'Catch browser automation before it reaches your users.',
    body: (
      <p>
        Identify headless browsers, automation frameworks, and scripted sessions running inside the browser, so you can
        stop <b>scraping</b>, <b>credential stuffing</b>, and <b>account takeover</b> without adding friction for real
        users.
      </p>
    ),
    previewTitle: 'Top automation tools',
    previewLabel: 'Preview of the browser automation tools detected most often across the workspace.',
    previewBots: AUTOMATION_TOOL_NAMES,
    docsUrl: DOCS_BOTD_QUICK_START_URL,
  },
}

function shuffle<T>(items: readonly T[]): T[] {
  const shuffled = [...items]

  // Fisher–Yates shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled
}

function pickRandomPreviewRows(pool: readonly PreviewBot[], count: number): PreviewRow[] {
  const bots = shuffle(pool).slice(0, count)
  const leaderCount = PREVIEW_LEADER_MIN + Math.floor(Math.random() * (PREVIEW_LEADER_MAX - PREVIEW_LEADER_MIN + 1))

  return bots.map((item, index) => {
    const percentage = PREVIEW_PERCENTAGES[index] ?? PREVIEW_PERCENTAGES.at(-1)!
    return {
      ...item,
      percentage,
      value: Math.max(1, Math.round(leaderCount * percentage)).toLocaleString(),
    }
  })
}

function AutomationIntelligenceIcon() {
  return (
    <span className='inline-flex items-center justify-center p-[3px] bg-gray-200 rounded-lg shadow-inner'>
      <span className='flex items-center justify-center size-12 bg-white border rounded-md'>
        <GridIcon.GoL />
      </span>
    </span>
  )
}

function PreviewDistribution({ title, items }: { title: string; items: PreviewRow[] }) {
  return (
    <Distribution.Root>
      <div className='flex items-center justify-between p-3 pl-4 border-b'>
        <Typography weight='medium'>{title}</Typography>
        <Badge tone='secondary'>Demo data</Badge>
      </div>
      <Distribution.Body className='h-32'>
        <div role='list' className='flex flex-col gap-2 w-full py-3'>
          {items.map((item, index) => (
            <Distribution.Row
              key={item.label}
              index={index}
              label={item.label}
              value={item.value}
              percentage={item.percentage}
              background='hsl(var(--fpds-color-gray-2))'
              labelColor='hsl(var(--fpds-color-gray-10))'
              className='pr-3'
              valueClassName='text-gray-800 font-mono font-normal text-right'
              prefix={<item.Logo size={16} />}
            />
          ))}
        </div>
      </Distribution.Body>
    </Distribution.Root>
  )
}

/**
 * The upsell panel itself, with no positioning of its own so it can be centred either over the whole
 * page (`BotDetectionUpsellModal`) or over a single section (`BotDetectionSourceUpsell`).
 */
function UpsellPanel({ subscriptionId, source }: { subscriptionId: string; source: BotDetectionSource }) {
  const { data: subscription } = useSubscription(subscriptionId)
  const pathname = useCurrentPathname()
  const content = UPSELL_CONTENT[source]
  // Re-randomise when the tab changes so each product shows its own preview rows.
  const [previewRows, setPreviewRows] = useState(() => pickRandomPreviewRows(content.previewBots, PREVIEW_ROW_COUNT))
  const [previewSource, setPreviewSource] = useState(source)
  const isEnterprise =
    subscription != null && getSubscriptionDisplayPlan(subscription) === SubscriptionDisplayPlan.Enterprise

  if (previewSource !== source) {
    setPreviewSource(source)
    setPreviewRows(pickRandomPreviewRows(content.previewBots, PREVIEW_ROW_COUNT))
  }

  return (
    <div className='relative flex flex-col overflow-hidden w-full max-w-xl max-h-[min(672px,90svh)] bg-gray-100 border border-black/10 rounded-xl shadow-2xl'>
      <Dialog className='flex flex-col overflow-y-auto h-full outline-0 outline-none'>
        <DialogBody>
          <div className='flex items-center gap-4 w-full'>
            <AutomationIntelligenceIcon />
            <div>
              <DialogTitle className='mt-0'>{content.title}</DialogTitle>
              <Typography as='p' className='text-gray-800'>
                {content.description}
              </Typography>
            </div>
          </div>
          <article className='prose'>{content.body}</article>
          <div role='img' aria-label={content.previewLabel} className='shrink-0 overflow-hidden mask-b-from-50%'>
            <PreviewDistribution title={content.previewTitle} items={previewRows} />
          </div>
        </DialogBody>
        <DialogFooter>
          <LinkButton href={content.docsUrl} target='_blank' rel='noreferrer' variant='secondary'>
            Learn more
            <ExternalLink size={16} />
          </LinkButton>
          {isEnterprise ? (
            <LinkButton
              href={SUPPORT_PAGE_URL}
              target='_blank'
              rel='noreferrer'
              variant='primary'
              onPress={() => ampli.appCtaClicked({ 'App Page Path': pathname, ctaName: 'Contact us' })}
            >
              Contact us
            </LinkButton>
          ) : (
            <LinkButtonLink
              to={AppRoute.SubscriptionPlan}
              params={{ subscriptionId }}
              variant='primary'
              onPress={() => ampli.appCtaClicked({ 'App Page Path': pathname, ctaName: 'Upgrade' })}
            >
              Upgrade
            </LinkButtonLink>
          )}
        </DialogFooter>
      </Dialog>
    </div>
  )
}

/**
 * Full-page upsell, portalled over the whole main column. Used when the workspace has no Bot
 * Detection access at all and no real page should mount.
 */
export function BotDetectionUpsellModal({ isOpen, subscriptionId, source = 'edge' }: BotDetectionUpsellModalProps) {
  if (!isOpen) {
    return null
  }

  // Portal a non-modal RAC Overlay to the page content so the outside UI remains interactive.
  // RAC's ModalOverlay would inert the surrounding UI and trap focus app-wide.
  return (
    <Overlay disableFocusManagement>
      <div
        data-testid='bot-detection-upsell-modal'
        className='absolute inset-0 isolate z-1201 flex items-center justify-center w-full h-(--visual-viewport-height) p-4 bg-linear-to-b to-gray-100 animate-in fade-in duration-300'
      >
        <UpsellPanel key={source} subscriptionId={subscriptionId} source={source} />
      </div>
    </Overlay>
  )
}

/**
 * Section-scoped upsell for the Overview page, shown when the *selected* source tab is not
 * entitled. Rendered inline (no portal) inside a `relative` wrapper around the data area, so the
 * filters, date picker and source tabs above it stay visible and clickable — a workspace entitled
 * to only one source has to be able to switch back to it.
 */
export function BotDetectionSourceUpsell({
  subscriptionId,
  source,
  showGradient = true,
  gradientClass,
}: {
  subscriptionId: string
  source: BotDetectionSource
  showGradient?: boolean
  gradientClass?: string
}) {
  const gradientClasses = gradientClass || (showGradient ? 'bg-linear-to-b from-white/60 to-gray-100' : '')

  return (
    <div
      data-testid='bot-detection-source-upsell'
      className={`-mx-6 absolute inset-0 isolate z-20 flex items-center justify-center overflow-hidden p-4 animate-in fade-in duration-300 ${gradientClasses}`}
    >
      <UpsellPanel subscriptionId={subscriptionId} source={source} />
    </div>
  )
}
